import React from 'react'

import { useDecision } from '@optimizely/react-sdk'
import Loading from 'Loading'
import type { Location } from 'history'
import { useDispatch, useSelector } from 'react-redux'
import { browserHistory } from 'react-router'

import {
  CONTROL_VIEW_CTA_INTENTS,
  GoustoAnytimeDashboard,
  MyGoustoPage,
} from '@features/my-gousto-page'
import { MyHomePage } from '@features/my-home-page'

import { actionTypes } from 'actions/actionTypes'
import loginActions from 'actions/login'
import { startOneOffOrder } from 'actions/oneOffOrder'
import actions from 'actions/user'
import config from 'config/routes'
import { getUserSubscriptionState } from 'selectors/user'
import { addTargetToRedirect } from 'utils/routes'
import { documentLocation } from 'utils/window'

import { ReferAFriendContainer } from './ReferAFriendContainer'
import { GoustoAnytimeModal } from './experiments/GoustoAnytime/GoustoAnytimeModal'
import { RESUBSCRIBE_URL } from './experiments/GoustoAnytime/intents'
import { useGoustoAnytimeDashboard } from './experiments/GoustoAnytime/useGoustoAnytimeDashboard'
import { useGoustoAnytimeExperiment } from './experiments/GoustoAnytime/useGoustoAnytimeExperiment'
import { useGoustoAnytimeIntentHandler } from './experiments/GoustoAnytime/useGoustoAnytimeIntentHandler'
import { useGoustoAnytimeOrderActions } from './experiments/GoustoAnytime/useGoustoAnytimeOrderActions'

type MyGoustoPageProps = {
  dispatchCancelOrder: (orderId: string) => void
  dispatchRefreshOrdersState: () => void
  // Only passed for unsubscribed customers; active subscribers never receive it, so the CTAs stay hidden.
  onCtaActionClick?: (intent: string) => void
}

type ActiveSubscriptionMyGoustoProps = Omit<MyGoustoPageProps, 'onCtaActionClick'> & {
  dispatchExpiredSessionLogout: () => void
}

const MyGoustoControlView = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
  onCtaActionClick,
}: MyGoustoPageProps) => (
  <>
    {/*
      Keep this selector at the route level so app and Cypress tests can
      target the control branch without changing @features/my-gousto-page.
      The feature-modules CI job runs `yarn workspaces foreach` over changed
      `@features/*` workspaces since the branch point, so touching that
      workspace would make its isolated unit suite run as well. That is
      undesirable here because this route-only change would then inherit
      unrelated feature-workspace test/dependency failures.
    */}
    <div data-testid="my-gousto-page">
      <MyGoustoPage
        dispatchCancelOrder={dispatchCancelOrder}
        dispatchRefreshOrdersState={dispatchRefreshOrdersState}
        onCtaActionClick={onCtaActionClick}
      />
    </div>
    <ReferAFriendContainer />
  </>
)

const MyGoustoLoadingView = () => (
  <div
    aria-label="Loading My Gousto"
    data-testid="my-gousto-loading"
    role="status"
    style={{
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: '350px',
    }}
  >
    <Loading />
  </div>
)

const ActiveSubscriptionMyGousto = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
  dispatchExpiredSessionLogout,
}: ActiveSubscriptionMyGoustoProps) => {
  const [decision, isDecisionReady, hasDecisionTimedOut] = useDecision(
    'rockets_my_gousto_new_home_page_experiment',
  )

  if (!isDecisionReady && !hasDecisionTimedOut) {
    return <MyGoustoLoadingView />
  }

  if (decision.variationKey === 't1') {
    return (
      <div data-testid="my-home-page">
        <MyHomePage onSessionExpired={dispatchExpiredSessionLogout} />
      </div>
    )
  }

  return (
    <MyGoustoControlView
      dispatchCancelOrder={dispatchCancelOrder}
      dispatchRefreshOrdersState={dispatchRefreshOrdersState}
    />
  )
}

const UnsubscribedMyGousto = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
  onCtaActionClick,
}: MyGoustoPageProps) => {
  const { isEnabled, isLoading, sections } = useGoustoAnytimeDashboard()
  const { onAction } = useGoustoAnytimeIntentHandler()
  const orderActions = useGoustoAnytimeOrderActions()

  if (isEnabled && isLoading) {
    return <MyGoustoLoadingView />
  }

  if (isEnabled && sections) {
    return (
      <div data-testid="gousto-anytime-dashboard-view">
        <GoustoAnytimeDashboard
          sections={sections}
          onAction={onAction}
          orderActions={orderActions}
        />
      </div>
    )
  }

  return (
    <MyGoustoControlView
      dispatchCancelOrder={dispatchCancelOrder}
      dispatchRefreshOrdersState={dispatchRefreshOrdersState}
      onCtaActionClick={onCtaActionClick}
    />
  )
}

export const MyGousto = () => {
  const { showModal, data, dismiss } = useGoustoAnytimeExperiment()
  const dispatch = useDispatch()
  const userSubscriptionState = useSelector(getUserSubscriptionState)
  const isUserSubscriptionLoading = userSubscriptionState === undefined
  const dispatchCancelOrder = (orderId: string) => {
    dispatch({
      type: actionTypes.ORDER_CANCEL,
      orderId,
      restorable: false,
    })
  }
  const dispatchRefreshOrdersState = () => {
    dispatch(actions.userLoadOrdersForMyGousto(true))
  }
  const dispatchExpiredSessionLogout = React.useCallback(() => {
    const redirectPath = addTargetToRedirect({
      target: true,
      location: documentLocation() as Location,
      redirectUrl: config.client.home,
    })

    dispatch(loginActions.logoutUser({ redirectPath }))
  }, [dispatch])

  const handleCtaAction = (intent: string) => {
    if (intent === CONTROL_VIEW_CTA_INTENTS.resubscribe) {
      browserHistory.push(RESUBSCRIBE_URL)

      return
    }

    if (intent === CONTROL_VIEW_CTA_INTENTS.orderOneOffBox) {
      dispatch(startOneOffOrder() as any)
    }
  }

  return (
    <div>
      {showModal && data && <GoustoAnytimeModal data={data} onDismiss={dismiss} />}
      {isUserSubscriptionLoading ? (
        <MyGoustoLoadingView />
      ) : userSubscriptionState !== 'active' ? (
        <UnsubscribedMyGousto
          dispatchCancelOrder={dispatchCancelOrder}
          dispatchRefreshOrdersState={dispatchRefreshOrdersState}
          onCtaActionClick={handleCtaAction}
        />
      ) : (
        <ActiveSubscriptionMyGousto
          dispatchCancelOrder={dispatchCancelOrder}
          dispatchRefreshOrdersState={dispatchRefreshOrdersState}
          dispatchExpiredSessionLogout={dispatchExpiredSessionLogout}
        />
      )}
    </div>
  )
}
